import React, { useEffect, useState } from 'react';
import { getUniqueThemes, Theme } from '../utils/themeExtractor';

interface ThemeSelectorProps {
  selectedThemes: string[];
  onThemeToggle: (theme: string) => void;
  onClearAll: () => void;
}

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ 
  selectedThemes, 
  onThemeToggle, 
  onClearAll 
}) => {
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllSelected, setShowAllSelected] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [isTranscriptionCollapsed, setIsTranscriptionCollapsed] = useState(false);

  useEffect(() => {
    try {
      const themes = getUniqueThemes();
      setAvailableThemes(themes);
      
      // Initialize all groups as collapsed
      const initialCollapsedState = themes.reduce((acc, theme) => {
        if (!acc[theme.chapter]) {
          acc[theme.chapter] = true; // true = collapsed
        }
        return acc;
      }, {} as Record<string, boolean>);
      setCollapsedGroups(initialCollapsedState);
    } catch (error) {
      console.error('Error loading themes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleGroup = (chapterName: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [chapterName]: !prev[chapterName]
    }));
  };

  const toggleAllGroups = (collapsed: boolean) => {
    const newState = Object.keys(collapsedGroups).reduce((acc, chapter) => {
      acc[chapter] = collapsed;
      return acc;
    }, {} as Record<string, boolean>);
    setCollapsedGroups(newState);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Laden van onderwerpen...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-600">
            Geselecteerd: <span className="font-semibold text-blue-600">{selectedThemes.length}</span> onderwerp{selectedThemes.length !== 1 ? 'en' : ''}
          </p>
          {selectedThemes.length > 0 && (
            <button
              onClick={onClearAll}
              className="text-sm text-red-600 hover:text-red-800 underline font-medium"
            >
              Alles wissen
            </button>
          )}
        </div>
        {selectedThemes.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {(showAllSelected ? selectedThemes : selectedThemes.slice(0, 5)).map((theme) => (
              <span key={theme} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                {theme}
                <button
                  onClick={() => onThemeToggle(theme)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))}
            {selectedThemes.length > 5 && !showAllSelected && (
              <button
                onClick={() => setShowAllSelected(true)}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors"
              >
                +{selectedThemes.length - 5} meer
              </button>
            )}
            {selectedThemes.length > 5 && showAllSelected && (
              <button
                onClick={() => setShowAllSelected(false)}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors"
              >
                Minder tonen
              </button>
            )}
          </div>
        )}
      </div>

      <div className="max-h-60 overflow-y-auto space-y-4 pr-2">
        {(() => {
          // Group themes by chapter
          const groupedThemes = availableThemes.reduce((groups, theme) => {
            if (!groups[theme.chapter]) {
              groups[theme.chapter] = [];
            }
            groups[theme.chapter].push(theme);
            return groups;
          }, {} as Record<string, typeof availableThemes>);

          return Object.entries(groupedThemes).map(([chapterName, themes]) => {
            const selectedInGroup = themes.filter(theme => selectedThemes.includes(theme.name));
            const allSelected = selectedInGroup.length === themes.length;
            const someSelected = selectedInGroup.length > 0 && selectedInGroup.length < themes.length;

            const handleGroupToggle = () => {
              if (allSelected) {
                // Deselect all in group
                themes.forEach(theme => {
                  if (selectedThemes.includes(theme.name)) {
                    onThemeToggle(theme.name);
                  }
                });
              } else {
                // Select all in group
                themes.forEach(theme => {
                  if (!selectedThemes.includes(theme.name)) {
                    onThemeToggle(theme.name);
                  }
                });
              }
            };

            return (
              <div key={chapterName} className="space-y-2">
                {/* Group Header */}
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleGroup(chapterName)}
                      className="flex items-center justify-center w-5 h-5 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      {collapsedGroups[chapterName] ? (
                        <span className="text-sm">▶</span>
                      ) : (
                        <span className="text-sm">▼</span>
                      )}
                    </button>
                    <h4 className="font-semibold text-gray-900 text-sm">{chapterName}</h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600">
                      {selectedInGroup.length} van {themes.length} geselecteerd
                    </span>
                    <button
                      onClick={handleGroupToggle}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        allSelected
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      {allSelected ? 'Alles deselecteren' : 'Alles selecteren'}
                    </button>
                  </div>
                </div>

                {/* Group Items - Collapsible */}
                {!collapsedGroups[chapterName] && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 ml-4">
                    {themes.map((theme) => (
                      <label key={theme.id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors border border-gray-200 hover:border-blue-300">
                        <input
                          type="checkbox"
                          checked={selectedThemes.includes(theme.name)}
                          onChange={() => onThemeToggle(theme.name)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="text-gray-900">
                            <span className="text-sm text-gray-600">
                              {theme.page}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

export default ThemeSelector; 